import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type EdgeSelectionChange,
  type Connection,
  type OnEdgesChange,
  type OnEdgeUpdateFunc,
  type OnNodesChange,
  type NodeSelectionChange,
  type NodeDimensionChange,
  useReactFlow,
} from 'reactflow';
import { useHotkeys } from 'react-hotkeys-hook';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useWorkflowStore } from '@/state/workflow.state';
import { FF_REMOVE_FROM_GROUP_WITH_MODIFIER } from '@/consts/featureFlags';
import { NodeType } from '@/enums/node-type.enum';
import { categorizeNodesGroupStatus } from '@/components/Nodes/utils/nodeUtils';
import { useFlowView } from '../FlowContext';
import { useGroupAttachementsHighlighting } from './useGroupNodeHighlighting';
import type { Edge, Node } from '@/types/node';

const useGroupInteractionContext = () => {
  const [isModifierPressed, setIsModifierPressed] = useState(false);
  const isRemoveFromGroupWithModifierEnabled = useFeatureFlagEnabled(FF_REMOVE_FROM_GROUP_WITH_MODIFIER);

  useHotkeys(
    'meta,ctrl',
    (event) => {
      if (isRemoveFromGroupWithModifierEnabled) {
        setIsModifierPressed(event.metaKey || event.ctrlKey);
      }
    },
    {
      keydown: isRemoveFromGroupWithModifierEnabled,
      keyup: isRemoveFromGroupWithModifierEnabled,
    },
  );

  useEffect(() => {
    // Clear on focus loss (covers Cmd+Tab, window switches, etc.)
    const handleBlur = () => setIsModifierPressed(false);
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') setIsModifierPressed(false);
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setIsModifierPressed]);

  return { isModifierPressed };
};

export const useReactFlowProps = () => {
  const flowView = useFlowView();
  const { screenToFlowPosition } = useReactFlow();

  const { isModifierPressed } = useGroupInteractionContext();
  const { setSuppressedSelectionNode, clearNodeIdToSuppressSelection, suppressedSelectionNode } = useWorkflowStore();

  const { highlightPotentialGroupAttachements, clearAttachementHighlights } = useGroupAttachementsHighlighting();

  const newConnectionRef = useRef<{
    source: string;
    target: string;
    sourceHandle: string;
    targetHandle: string;
  }>(undefined);
  const setInvalidConnection = useWorkflowStore((state) => state.setInvalidConnection);

  const isValidConnection = useCallback(
    ({ source, target, sourceHandle, targetHandle }: Connection) => {
      if (!source || !target) return false;
      if (flowView.wouldConnectionResultInCycle(source, target)) {
        setInvalidConnection({
          errorId: source === target ? 'self-connection' : 'cyclic',
          source,
          target,
          sourceHandle,
          targetHandle,
        });
        return false;
      }
      return true;
    },
    [flowView, setInvalidConnection],
  );

  const onEdgeUpdateStart = useCallback((_, _edge: Edge) => {
    // TODO ongoing remove
  }, []);
  const onEdgeUpdate: OnEdgeUpdateFunc = useCallback((_oldEdge: Edge, newConnection: Connection) => {
    const { source, target, sourceHandle, targetHandle } = newConnection;
    if (source !== null && target !== null && sourceHandle !== null && targetHandle !== null) {
      newConnectionRef.current = {
        source,
        target,
        sourceHandle,
        targetHandle,
      };
    }
  }, []);

  const onEdgeUpdateEnd = useCallback(
    (_e: MouseEvent, oldEdge: Edge) => {
      const newConnection = newConnectionRef.current;
      if (newConnection) {
        flowView.reconnectEdge(
          newConnection.source,
          newConnection.target,
          newConnection.sourceHandle,
          newConnection.targetHandle,
          oldEdge,
        );
      } else {
        flowView.deleteEdges([oldEdge.id]);
      }

      newConnectionRef.current = undefined;
    },
    [flowView],
  );

  const onNodesChange = useCallback(
    (changes: Parameters<OnNodesChange>[0]) => {
      const selectionChanges: NodeSelectionChange[] = changes.filter((c) => c.type === 'select');
      if (selectionChanges.length > 0) {
        const selectedNodes = flowView.selectedNodes;
        for (const change of selectionChanges) {
          const { id, selected } = change;
          if (selected) {
            selectedNodes.push(id);
          } else {
            selectedNodes.splice(selectedNodes.indexOf(id), 1);
          }
        }
        flowView.selectedNodes = selectedNodes;
      }

      const dimensionChanges = changes.filter(
        (c): c is NodeDimensionChange => c.type === 'dimensions' && c.dimensions !== undefined,
      );

      if (dimensionChanges.length > 0) {
        flowView.setNodeDimensions(
          dimensionChanges.map((c) => ({
            nodeId: c.id,
            width: c.dimensions!.width,
            height: c.dimensions!.height,
          })),
        );
      }

      // Note: Other change types are not handled here
    },
    [flowView],
  );

  const onEdgesChange = useCallback(
    (changes: Parameters<OnEdgesChange>[0]) => {
      const selectionChanges: EdgeSelectionChange[] = changes.filter((c) => c.type === 'select');
      if (selectionChanges.length === 0) return;

      const selectedEdges = flowView.selectedEdges;
      for (const { id, selected } of selectionChanges) {
        if (selected) {
          selectedEdges.push(id);
        } else {
          selectedEdges.splice(selectedEdges.indexOf(id), 1);
        }
      }
      flowView.selectedEdges = selectedEdges;
    },
    [flowView],
  );

  const onConnect = useCallback(
    ({ source, target, sourceHandle, targetHandle }: Connection) => {
      if (!source || !target || !sourceHandle || !targetHandle) return;
      flowView.connectEdge(source, target, sourceHandle, targetHandle);
    },
    [flowView],
  );

  const onNodeDrag = useCallback(
    (_: unknown, node: Node) => {
      // Always update positions immediately (must be smooth)
      flowView.handleNodeDrag([node], isModifierPressed);

      // Throttled highlighting (can be delayed without affecting UX)
      // Only highlight for group nodes and ungrouped nodes (grouped nodes don't need highlighting)
      if (!isModifierPressed && (node.type === NodeType.CustomGroup || !node.parentId)) {
        const { groupNodes, ungroupedNodes } = categorizeNodesGroupStatus([node]);
        highlightPotentialGroupAttachements(groupNodes, ungroupedNodes);
      }
    },
    [flowView, isModifierPressed, highlightPotentialGroupAttachements],
  );

  const onNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      // Cancel any pending throttled highlights
      highlightPotentialGroupAttachements.cancel();

      flowView.handleNodeDrag([node], isModifierPressed, false);

      // Clear highlights after drag stop
      clearAttachementHighlights();
    },
    [flowView, isModifierPressed, clearAttachementHighlights, highlightPotentialGroupAttachements],
  );

  const onSelectionDrag = useCallback(
    (_: unknown, nodes: Node[]) => {
      // Always update positions immediately (must be smooth)
      flowView.handleNodeDrag(nodes, isModifierPressed);

      // Throttled highlighting (can be delayed without affecting UX)
      // Filter to only nodes that need highlighting (groups and ungrouped nodes)
      if (!isModifierPressed) {
        const nodesToHighlight = nodes.filter((node) => node.type === NodeType.CustomGroup || !node.parentId);
        if (nodesToHighlight.length > 0) {
          const { groupNodes, ungroupedNodes } = categorizeNodesGroupStatus(nodesToHighlight);
          highlightPotentialGroupAttachements(groupNodes, ungroupedNodes);
        }
      }
    },
    [flowView, isModifierPressed, highlightPotentialGroupAttachements],
  );

  const onSelectionDragStop = useCallback(
    (_: unknown, nodes: Node[]) => {
      // Cancel any pending throttled highlights
      highlightPotentialGroupAttachements.cancel();

      flowView.handleNodeDrag(nodes, isModifierPressed, false);

      // Clear highlights after drag stop
      clearAttachementHighlights();
    },
    [flowView, isModifierPressed, clearAttachementHighlights, highlightPotentialGroupAttachements],
  );

  const CLICK_THRESHOLD = 5; // pixels - treat as click if movement less than this

  /**
   * These selection handlers support multi-selecting group children using drag.
   * They both trigger selection if drag distance is < CLICK_THRESHOLD and also prevent the group itself from
   * being selected while children multiselection is in progress.
   */
  const onSelectionStart = useCallback(
    (event: MouseEvent) => {
      const flowPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const nodeAtPosition = flowView.findNodeAtPosition(flowPosition);

      if (nodeAtPosition && nodeAtPosition.type === NodeType.CustomGroup) {
        const isAlreadySelected = flowView.isNodeSelected(nodeAtPosition.id);
        if (!isAlreadySelected) {
          setSuppressedSelectionNode(nodeAtPosition.id, event.clientX, event.clientY);
        }
      }
    },
    [setSuppressedSelectionNode, flowView, screenToFlowPosition],
  );

  /**
   * This handler also supports multi-selecting group children using drag.
   * It checks if the selection was a click on an unselected group node and if so, selects the group node.
   * It also clears the suppressed selection node.
   */
  const onSelectionEnd = useCallback(
    (event: MouseEvent) => {
      // Check if this was a click (not drag) on an unselected group node
      if (suppressedSelectionNode) {
        const dx = event.clientX - suppressedSelectionNode.startX;
        const dy = event.clientY - suppressedSelectionNode.startY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // If movement was minimal, select the group node
        if (distance < CLICK_THRESHOLD) {
          flowView.selectedNodes = [suppressedSelectionNode.nodeId];
        }
      }

      clearNodeIdToSuppressSelection();
    },
    [clearNodeIdToSuppressSelection, flowView, suppressedSelectionNode],
  );

  return {
    onConnect,
    onEdgeUpdateStart,
    onEdgeUpdate,
    onEdgeUpdateEnd,
    onNodesChange,
    onEdgesChange,
    onNodeDragStart: onNodeDrag,
    onNodeDrag,
    onNodeDragStop,
    onSelectionDragStart: onSelectionDrag,
    onSelectionDrag,
    onSelectionDragStop,
    onSelectionStart,
    onSelectionEnd,
    isValidConnection,
  };
};
